/* Crafts - search.js
   Predictive search controller. AbortController fetch + Map cache +
   ARIA combobox keyboard nav + localStorage recent searches + mobile
   full-screen overlay. */
(function () {
  'use strict';

  var MIN_LEN = 3;
  var DEBOUNCE = 300;
  var MAX_RECENT = 8;
  var STORAGE = 'crafts.recent_searches';
  var ENDPOINT = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
  var TYPES = 'product,query,collection,article';
  var FIELDS = 'title,product_type,vendor,tag';
  var LIMIT = 4;

  var cache = new Map();
  var trending = readTrending();
  var sessions = []; // { input, panel, status, form, controller, debounceTimer, active }

  function readTrending() {
    var raw = document.querySelector('meta[name="predictive-trending"]');
    if (!raw) return [];
    return (raw.content || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean).slice(0, 6);
  }

  function readRecent() {
    try { return JSON.parse(localStorage.getItem(STORAGE) || '[]'); } catch (e) { return []; }
  }
  function writeRecent(list) {
    try { localStorage.setItem(STORAGE, JSON.stringify(list.slice(0, MAX_RECENT))); } catch (e) {}
  }
  function pushRecent(term) {
    term = (term || '').trim();
    if (!term) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    writeRecent(list);
  }
  function removeRecent(term) {
    writeRecent(readRecent().filter(function (t) { return t !== term; }));
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderPrePanel(panel) {
    var recent = readRecent();
    var parts = [];
    if (recent.length) {
      parts.push('<div class="predictive__pre">');
      parts.push('<p class="predictive__heading">Recent</p>');
      parts.push('<ul class="predictive__chips" role="list">');
      recent.slice(0, 5).forEach(function (t) {
        parts.push(
          '<li class="predictive__chip">' +
            '<a href="/search?q=' + encodeURIComponent(t) + '" data-predictive-recent>' + escapeHtml(t) + '</a>' +
            '<a href="#" role="button" class="predictive__chip-x" aria-label="Remove" data-predictive-remove="' + escapeHtml(t) + '">×</a>' +
          '</li>'
        );
      });
      parts.push('</ul></div>');
    }
    if (trending.length) {
      parts.push('<div class="predictive__pre">');
      parts.push('<p class="predictive__heading">Trending</p>');
      parts.push('<ul class="predictive__chips" role="list">');
      trending.forEach(function (t) {
        parts.push('<li class="predictive__chip predictive__chip--trending"><a href="/search?q=' + encodeURIComponent(t) + '">' + escapeHtml(t) + '</a></li>');
      });
      parts.push('</ul></div>');
    }
    if (!parts.length) {
      parts.push('<div class="predictive__empty"><p class="predictive__empty-title">Start typing to search</p><p class="predictive__empty-hint">Products, kits, blog posts and more.</p></div>');
    }
    panel.innerHTML = parts.join('');
  }

  function setExpanded(s, open) {
    s.input.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (s.panel) s.panel.hidden = !open;
    document.documentElement.classList.toggle('search-open', open);
    var dimmer = document.querySelector('[data-search-dimmer]');
    if (dimmer) dimmer.hidden = !open;
  }

  function options(s) {
    return Array.from(s.panel.querySelectorAll('[data-predictive-option]'));
  }
  function clearActive(s) {
    options(s).forEach(function (o) { o.setAttribute('aria-selected', 'false'); o.classList.remove('is-active'); });
    s.input.removeAttribute('aria-activedescendant');
    s.active = -1;
  }
  function setActive(s, i) {
    var opts = options(s);
    if (!opts.length) return;
    var n = opts.length;
    s.active = ((i % n) + n) % n;
    opts.forEach(function (o, idx) {
      var isMatch = idx === s.active;
      o.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      o.classList.toggle('is-active', isMatch);
      if (isMatch) {
        s.input.setAttribute('aria-activedescendant', o.id);
        try { o.scrollIntoView({ block: 'nearest' }); } catch (e) {}
      }
    });
  }
  function navigateActive(s) {
    var opts = options(s);
    if (s.active >= 0 && opts[s.active]) {
      var href = opts[s.active].getAttribute('data-href');
      if (href) { pushRecent(s.input.value); window.location.href = href; return true; }
    }
    return false;
  }

  function announceCount(s) {
    if (!s.status) return;
    var panel = s.panel.querySelector('[data-predictive-panel]');
    var total = panel ? parseInt(panel.getAttribute('data-total') || '0', 10) : 0;
    if (total === 0) {
      s.status.textContent = 'No results';
    } else {
      s.status.textContent = total + ' suggestion' + (total === 1 ? '' : 's') + ' available';
    }
  }

  function fetchPredictive(s, q) {
    var key = q.toLowerCase().trim();
    if (cache.has(key)) {
      s.panel.innerHTML = cache.get(key);
      clearActive(s);
      announceCount(s);
      return Promise.resolve();
    }
    if (s.controller) { try { s.controller.abort(); } catch (e) {} }
    if ('AbortController' in window) s.controller = new AbortController();
    var url = ENDPOINT +
      '?q=' + encodeURIComponent(q) +
      '&resources[type]=' + encodeURIComponent(TYPES) +
      '&resources[limit]=' + LIMIT +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' + encodeURIComponent(FIELDS) +
      '&section_id=predictive-search';
    var skeletonTimer = setTimeout(function () { s.panel.innerHTML = skeletonHtml(); }, 200);
    return fetch(url, {
      headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      signal: s.controller ? s.controller.signal : undefined
    })
    .then(function (r) {
      clearTimeout(skeletonTimer);
      if (r.status === 429) {
        var retry = parseInt(r.headers.get('Retry-After') || '2', 10);
        s.muteUntil = Date.now() + retry * 1000;
        s.panel.innerHTML = '<div class="predictive__empty"><p class="predictive__empty-title">Too many requests</p><p class="predictive__empty-hint">Pausing for a moment.</p></div>';
        return null;
      }
      if (!r.ok) throw new Error('search ' + r.status);
      return r.text();
    })
    .then(function (html) {
      if (!html) return;
      var wrap = document.createElement('div');
      wrap.innerHTML = html;
      var fresh = wrap.querySelector('[data-predictive-panel]');
      if (fresh) {
        var content = fresh.outerHTML;
        cache.set(key, content);
        s.panel.innerHTML = content;
        clearActive(s);
        announceCount(s);
      }
    })
    .catch(function (err) {
      clearTimeout(skeletonTimer);
      if (err && err.name === 'AbortError') return;
      s.panel.innerHTML = '<div class="predictive__empty"><p class="predictive__empty-title">Search unavailable</p><p class="predictive__empty-hint">Check your connection and try again.</p></div>';
    });
  }

  function skeletonHtml() {
    var rows = '';
    for (var i = 0; i < 4; i++) rows += '<div class="predictive__skel-row"><span class="predictive__skel-thumb"></span><span class="predictive__skel-lines"><span></span><span></span></span></div>';
    return '<div class="predictive__skeleton" aria-hidden="true">' + rows + '</div>';
  }

  function handleInput(s) {
    var q = s.input.value.trim();
    if (s.muteUntil && Date.now() < s.muteUntil) return;
    if (q.length < MIN_LEN) {
      renderPrePanel(s.panel);
      setExpanded(s, true);
      clearActive(s);
      return;
    }
    clearTimeout(s.debounceTimer);
    s.debounceTimer = setTimeout(function () {
      fetchPredictive(s, q).then(function () { setExpanded(s, true); });
    }, DEBOUNCE);
  }

  function handleKey(s, e) {
    var key = e.key;
    if (key === 'ArrowDown') { e.preventDefault(); setActive(s, s.active < 0 ? 0 : s.active + 1); return; }
    if (key === 'ArrowUp')   { e.preventDefault(); setActive(s, s.active < 0 ? options(s).length - 1 : s.active - 1); return; }
    if (key === 'Home')      { e.preventDefault(); setActive(s, 0); return; }
    if (key === 'End')       { e.preventDefault(); setActive(s, options(s).length - 1); return; }
    if (key === 'Enter')     { if (navigateActive(s)) e.preventDefault(); else pushRecent(s.input.value); return; }
    if (key === 'Escape')    { e.preventDefault(); setExpanded(s, false); s.input.focus(); return; }
    if (key === 'Tab')       { setExpanded(s, false); return; }
  }

  function attach(input) {
    if (!input || input.dataset.predictiveBound) return;
    input.dataset.predictiveBound = '1';
    var form = input.closest('form');
    var panelId = input.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;
    var status = panel.parentNode.querySelector('[data-predictive-status]');
    var s = { input: input, panel: panel, status: status, form: form, controller: null, debounceTimer: null, active: -1, muteUntil: 0 };
    sessions.push(s);

    input.addEventListener('input', function () { handleInput(s); });
    input.addEventListener('focus', function () { handleInput(s); });
    input.addEventListener('keydown', function (e) { handleKey(s, e); });

    if (form) form.addEventListener('submit', function () { pushRecent(input.value); });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-predictive-remove]')) {
        var term = e.target.getAttribute('data-predictive-remove');
        removeRecent(term);
        renderPrePanel(s.panel);
        e.preventDefault();
        return;
      }
      var chip = e.target.closest('[data-predictive-recent], .predictive__chip a');
      if (chip) { pushRecent(chip.textContent.trim()); return; }
      var opt = e.target.closest('[data-predictive-option]');
      if (opt) { pushRecent(input.value); return; }
      if (!e.target.closest('.header__search, .menu-drawer__search, [data-predictive-results], [data-search-overlay]')) {
        setExpanded(s, false);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && input.getAttribute('aria-expanded') === 'true') {
        setExpanded(s, false);
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-predictive-input]').forEach(attach);
    var dimmer = document.querySelector('[data-search-dimmer]');
    if (dimmer) dimmer.addEventListener('click', function () { sessions.forEach(function (s) { setExpanded(s, false); }); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
